import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { formatDate } from "../utils/general_util";

const DraftTicket = () => {
    const navigate = useNavigate();

    const [createdBy, setCreatedBy] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [search, setSearch] = useState("");
    const [sortOrder, setSortOrder] = useState("desc"); // 'desc' | 'asc'

    // --------------------------------------------------
    // Read email from localStorage to use as created_by
    // --------------------------------------------------
    useEffect(() => {
        if (typeof window === "undefined") return;

        const email =
            window.localStorage.getItem("userEmail") ||
            window.localStorage.getItem("email");

        if (!email) {
            setError(
                "No user email found in localStorage. Draft tickets cannot be loaded."
            );
            setLoading(false);
            return;
        }

        setCreatedBy(email);
    }, []);

    // --------------------------------------------------
    // Fetch draft tickets for this user
    // --------------------------------------------------
    useEffect(() => {
        if (!createdBy) return;

        const fetchDrafts = async () => {
            setLoading(true);
            setError("");
            try {
                const res = await axiosInstance.get("/ticket/", {
                    params: {
                        status: "draft",
                        created_by: createdBy,
                    },
                });
                setTickets(res.data || []);
            } catch (err) {
                console.error("Failed to load draft tickets:", err);
                setError("Failed to load draft tickets. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchDrafts();
    }, [createdBy]);

    // --------------------------------------------------
    // Filter + sort in the UI
    // --------------------------------------------------
    const filteredAndSorted = useMemo(() => {
        let list = Array.isArray(tickets) ? [...tickets] : [];

        const term = search.trim().toLowerCase();
        if (term) {
            const isNumeric = /^\d+$/.test(term);
            list = list.filter((t) => {
                const title = (t.title || "").toLowerCase();
                const idStr = String(t.ticketId || "");
                if (isNumeric) {
                    return idStr.includes(term) || title.includes(term);
                }
                return title.includes(term);
            });
        }

        list.sort((a, b) => {
            const da = a.createdAt ? new Date(a.createdAt) : new Date(0);
            const db = b.createdAt ? new Date(b.createdAt) : new Date(0);
            if (sortOrder === "asc") {
                return da - db;
            }
            return db - da;
        });

        return list;
    }, [tickets, search, sortOrder]);

    // --------------------------------------------------
    // Helpers
    // --------------------------------------------------

    function handleOpenTicket(t) {
        if (!t.ticketId) return;
        // Adjust route if your router uses a different path
        navigate(`/edit-ticket/${t.ticketId}`);
    }

    // --------------------------------------------------
    // UI
    // --------------------------------------------------
    return (
        <div className="px-4 py-6 sm:px-6 lg:px-10">
            <div className="mx-auto max-w-6xl rounded-3xl bg-white/95 p-8 shadow-2xl ring-1 ring-slate-200">
                {/* Header */}
                <header className="mb-6 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-slate-900 text-left">
                            Draft Tickets
                        </h1>
                        <p className="mt-1 text-sm text-slate-600 text-left">
                            Tickets you have created but have not submitted for review yet.
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-xs sm:text-xs">
                        {createdBy && (
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">
                                Logged as: {createdBy}
                            </span>
                        )}
                    </div>
                </header>

                {/* Error banner */}
                {error && (
                    <div className="mb-4 rounded-md border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-800">
                        {error}
                    </div>
                )}

                {/* Controls */}
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex-1">
                        <label className="mb-1 block text-xs font-semibold text-slate-700">
                            Search by title or Ticket ID
                        </label>
                        <input
                            type="text"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                            placeholder="e.g. 102 or 'Production efficiency'"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="w-full sm:w-56">
                        <label className="mb-1 block text-xs font-semibold text-slate-700">
                            Sort By date
                        </label>
                        <select
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                        >
                            <option value="desc">Newest first</option>
                            <option value="asc">Oldest first</option>
                        </select>
                    </div>
                </div>

                {/* Content */}
                {loading && (
                    <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
                        Loading draft tickets...
                    </div>
                )}

                {!loading && !error && filteredAndSorted.length === 0 && (
                    <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
                        No draft tickets found for your account.
                    </div>
                )}

                {!loading && !error && filteredAndSorted.length > 0 && (
                    <div className="mt-4 space-y-3">
                        {filteredAndSorted.map((t) => (
                            <article
                                key={t.ticketId}
                                onClick={() => handleOpenTicket(t)}
                                className="cursor-pointer rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition hover:-translate-y-[1px] hover:border-slate-300 hover:shadow-md"
                            >
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                    {/* Left: ticket info */}
                                    <div className="min-w-0 flex-1 space-y-1.5 text-left">
                                        <div className="flex items-center gap-2">
                                            <p className="text-xs sm:text-sm text-slate-500">
                                                Ticket ID:{" "}
                                                <span className="font-semibold text-slate-800">
                                                    {t.ticketId ?? "N/A"}
                                                </span>
                                            </p>

                                            {t.comment && (
                                                <span className="inline-flex items-center rounded-full bg-red-500 px-2.5 py-1 text-xs font-medium text-white shadow-sm">
                                                    Changes Requested - Feedback Provided 💬
                                                </span>
                                            )}
                                        </div>

                                        <h2 className="line-clamp-2 text-sm font-semibold text-slate-900 sm:text-base">
                                            {t.title || "(No title)"}
                                        </h2>

                                        {t.description && (
                                            <p className="mt-1.5 line-clamp-3 break-words text-xs text-slate-600 sm:text-sm">
                                                {t.description}
                                            </p>
                                        )}

                                        <div className="text-xs text-slate-600">
                                            Created: {formatDate(t.createdAt)}
                                        </div>
                                    </div>

                                    {/* Right: status / meta */}
                                    <div className="mt-3 flex flex-row items-center justify-between gap-3 text-xs sm:mt-0 sm:flex-col sm:items-end sm:justify-start sm:text-xs">
                                        <div className="flex flex-row flex-wrap gap-2 sm:flex-col sm:items-end">
                                            <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-100 shadow-sm">
                                                Draft
                                            </span>

                                            {t.ticketType && (
                                                <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200 shadow-sm">
                                                    {t.ticketType === "reactive"
                                                        ? "Reactive"
                                                        : "Proactive"}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DraftTicket;
